import { useState, useEffect } from "react";
import { toast } from "sonner";
import { isValidDateRange } from "@/utils/formatDateForInput";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import Alert from "../common/Alert";
import CloseButton from "../common/CloseButton";
import { Save } from "lucide-react";
import { makeRequest } from "@/utils/axiosInterceptor";
import { useQueryClient } from "@tanstack/react-query";

interface ApiData {
  id: number;
  hospital_clinic_id: number;
  api_access: boolean;
  allowed_requests: number;
  chest_xray_count: number;
  abnormal_model_access: boolean;
  tb_model_access: boolean;
  consolidation_model_access: boolean;
  effusion_model_access: boolean;
  cardiomegaly_model_access: boolean;
  pneumothorax_model_access: boolean;
  rheumatic_model_access: boolean;
  edema_model_access: boolean;
  atelectasis_model_access: boolean;
  activation_date: string | null;
  expiry_date: string | null;
  segregation_model_access: boolean;
}

interface HospitalData {
  id: number;
  name: string;
  api: ApiData;
  [key: string]: any;
}

export default function ApiEditForm({
  selected,
  setSelected,
}: {
  selected: HospitalData;
  setSelected: (data: HospitalData | null) => void;
}) {
  const apiData = selected?.api;

  // Format ISO date string to datetime-local input format
  const formatDateForInput = (dateString: string | null): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  if (!apiData) {
    return (
      <div
        style={{
          width: "100%",
          height: "75vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <span>No API configured for this hospital</span>
      </div>
    );
  }

  const [editActive, setEditActive] = useState(false);
  const [currData, setCurrData] = useState<ApiData>(apiData);
  const [updateAlert, setUpdateAlert] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isModified = JSON.stringify(apiData) !== JSON.stringify(currData)
  const queryClient = useQueryClient();

  useEffect(() => {
    setCurrData(apiData);
  }, [apiData]);

  const updateApi = async () => {
    try {
      setIsSubmitting(true)
      if (!isValidDateRange(currData.activation_date, currData.expiry_date)) {
        toast.error('Start must be before end');
        setIsSubmitting(false);
        return;
      }
      console.log("Updating API");
      console.log(currData);
      const res = await makeRequest(
        "POST",
        `/apis/update/${currData.id}`,
        currData,
      );
      console.log(res);
      if (res?.status === 201 || res?.status === 200) {
        queryClient.invalidateQueries({
          queryKey: ["apis"],
        });
        setSelected({ ...selected, api: currData });
        setEditActive(false);
        setUpdateAlert(false);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsSubmitting(false)
    }
  };

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <div>
          <span style={{ fontWeight: "bold" }}>Editing API ID: </span>
          {currData.id}
        </div>
        <div style={{ display: "flex", gap: "0.5vw", alignItems: 'center' }}>
          {editActive && (
            <Button
              size="sm"
              onClick={() => setUpdateAlert(true)}
              className="flex items-center gap-2"
              disabled={isSubmitting}
            >
              <Save size={16} /> Save Changes
            </Button>
          )}
          <Button
            size="sm"
            variant={editActive ? "outline" : "default"}
            onClick={() => setEditActive(!editActive)}
            className="flex items-center gap-2"
          >
            {editActive ? "Cancel" : "Edit"}
          </Button>

          <CloseButton onClose={() => setSelected(null)} isModified={isModified} isSubmitting={isSubmitting} />
        </div>
      </div>
      <Separator className="mt-[1vh]" />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "row",
          width: "100%",
        }}
      >
        {/* Left Panel - API Configuration */}
        <div
          className=" p-2"
          style={{
            width: "50%",
            height: "75vh",
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
          }}
        >
          <h2 style={{ fontWeight: "bold" }} className="text-lg">
            API Configuration
          </h2>
          <Separator className="mt-[1vh]" />

          <div className="mt-[2vh]">
            <h5 style={{ fontWeight: "bold" }} className="text-sm">
              API Access
            </h5>
            <div className="mt-[1vh]">
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={currData.api_access}
                  onCheckedChange={(val) =>
                    setCurrData({ ...currData, api_access: val as boolean })
                  }
                  disabled={!editActive}
                  id="api_access"
                />
                <label
                  htmlFor="api_access"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Enable API Access
                </label>
              </div>
            </div>
          </div>

          {/* <div className="mt-[2vh]">
            <h5 style={{ fontWeight: "bold" }} className="text-sm">
              Allowed Tokens
            </h5>
            <Input
              onChange={(e) =>
                setCurrData({
                  ...currData,
                  allowed_requests: parseInt(e.target.value) || 0,
                })
              }
              disabled={!editActive}
              type="number"
              value={currData.allowed_requests}
              className="mt-[1vh]"
            />
          </div> */}

          <div className="mt-[2vh]">
            <h5 style={{ fontWeight: "bold" }} className="text-sm">
              Activation Date
            </h5>
            <Input
              onChange={(e) =>
                setCurrData({
                  ...currData,
                  activation_date: e.target.value
                    ? new Date(e.target.value).toISOString()
                    : null,
                })
              }
              disabled={!editActive}
              type="datetime-local"
              value={formatDateForInput(currData.activation_date)}
              className="mt-[1vh]"
            />
          </div>

          <div className="mt-[2vh]">
            <h5 style={{ fontWeight: "bold" }} className="text-sm">
              Expiry Date
            </h5>
            <Input
              onChange={(e) =>
                setCurrData({
                  ...currData,
                  expiry_date: e.target.value
                    ? new Date(e.target.value).toISOString()
                    : null,
                })
              }
              disabled={!editActive}
              type="datetime-local"
              value={formatDateForInput(currData.expiry_date)}
              className="mt-[1vh]"
            />
          </div>

          <div className="mt-[2vh]">
            <h2 style={{ fontWeight: "bold" }} className="text-lg">
              Model Access
            </h2>
            <Separator className="mt-[1vh]" />

            <div className="mt-[3vh]">
              <div className="flex items-center space-x-2">
                <Checkbox
                  disabled={!editActive}
                  checked={currData.abnormal_model_access}
                  onCheckedChange={(val) =>
                    setCurrData({
                      ...currData,
                      abnormal_model_access: val as boolean,
                    })
                  }
                  id="abnormal"
                />
                <label
                  htmlFor="abnormal"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Abnormal
                </label>
              </div>
            </div>

            <div className="mt-[1vh]">
              <div className="flex items-center space-x-2">
                <Checkbox
                  disabled={!editActive}
                  checked={currData.tb_model_access}
                  onCheckedChange={(val) =>
                    setCurrData({
                      ...currData,
                      tb_model_access: val as boolean,
                    })
                  }
                  id="tb"
                />
                <label
                  htmlFor="tb"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  TB
                </label>
              </div>
            </div>

            <div className="mt-[1vh]">
              <div className="flex items-center space-x-2">
                <Checkbox
                  disabled={!editActive}
                  checked={currData.atelectasis_model_access}
                  onCheckedChange={(val) =>
                    setCurrData({
                      ...currData,
                      atelectasis_model_access: val as boolean,
                    })
                  }
                  id="atelectasis"
                />
                <label
                  htmlFor="atelectasis"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Atelectasis
                </label>
              </div>
            </div>

            <div className="mt-[1vh]">
              <div className="flex items-center space-x-2">
                <Checkbox
                  disabled={!editActive}
                  checked={currData.edema_model_access}
                  onCheckedChange={(val) =>
                    setCurrData({
                      ...currData,
                      edema_model_access: val as boolean,
                    })
                  }
                  id="edema"
                />
                <label
                  htmlFor="edema"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Edema
                </label>
              </div>
            </div>

            <div className="mt-[1vh]">
              <div className="flex items-center space-x-2">
                <Checkbox
                  disabled={!editActive}
                  checked={currData.cardiomegaly_model_access}
                  onCheckedChange={(val) =>
                    setCurrData({
                      ...currData,
                      cardiomegaly_model_access: val as boolean,
                    })
                  }
                  id="cardiomegaly"
                />
                <label
                  htmlFor="cardiomegaly"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Cardiomegaly
                </label>
              </div>
            </div>

            <div className="mt-[1vh]">
              <div className="flex items-center space-x-2">
                <Checkbox
                  disabled={!editActive}
                  checked={currData.consolidation_model_access}
                  onCheckedChange={(val) =>
                    setCurrData({
                      ...currData,
                      consolidation_model_access: val as boolean,
                    })
                  }
                  id="consolidation"
                />
                <label
                  htmlFor="consolidation"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Consolidation
                </label>
              </div>
            </div>

            <div className="mt-[1vh]">
              <div className="flex items-center space-x-2">
                <Checkbox
                  disabled={!editActive}
                  checked={currData.rheumatic_model_access}
                  onCheckedChange={(val) =>
                    setCurrData({
                      ...currData,
                      rheumatic_model_access: val as boolean,
                    })
                  }
                  id="rheumatic"
                />
                <label
                  htmlFor="rheumatic"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Rheumatic
                </label>
              </div>
            </div>

            <div className="mt-[1vh]">
              <div className="flex items-center space-x-2">
                <Checkbox
                  disabled={!editActive}
                  checked={currData.pneumothorax_model_access}
                  onCheckedChange={(val) =>
                    setCurrData({
                      ...currData,
                      pneumothorax_model_access: val as boolean,
                    })
                  }
                  id="pneumothorax"
                />
                <label
                  htmlFor="pneumothorax"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Pneumothorax
                </label>
              </div>
            </div>

            <div className="mt-[1vh]">
              <div className="flex items-center space-x-2">
                <Checkbox
                  disabled={!editActive}
                  checked={currData.effusion_model_access}
                  onCheckedChange={(val) =>
                    setCurrData({
                      ...currData,
                      effusion_model_access: val as boolean,
                    })
                  }
                  id="effusion"
                />
                <label
                  htmlFor="effusion"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Effusion
                </label>
              </div>
            </div>

            <div className="mt-[1vh]">
              <div className="flex items-center space-x-2">
                <Checkbox
                  disabled={!editActive}
                  checked={currData.segregation_model_access}
                  onCheckedChange={(val) =>
                    setCurrData({
                      ...currData,
                      segregation_model_access: val as boolean,
                    })
                  }
                  id="segregation"
                />
                <label
                  htmlFor="segregation"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Segregation
                </label>
              </div>
            </div>
          </div>
        </div>

        <Separator orientation="vertical" className="mt-[1vh] h-[73vh]" />

        {/* Right Panel - Summary */}
        <div
          className=" p-2"
          style={{
            width: "50%",
            height: "75vh",
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
          }}
        >
          <h2 style={{ fontWeight: "bold" }} className="text-lg">
            Configuration Summary
          </h2>
          <Separator className="mt-[1vh]" />

          <div className="mt-[2vh]">
            <h5 style={{ fontWeight: "bold" }} className="text-sm">
              Hospital/Clinic ID
            </h5>
            <p className="text-sm text-gray-600 mt-[0.5vh]">
              {currData.hospital_clinic_id}
            </p>
          </div>

          <div className="mt-[2vh]">
            <h5 style={{ fontWeight: "bold" }} className="text-sm">
              API Access Status
            </h5>
            <p className="text-sm text-gray-600 mt-[0.5vh]">
              {currData.api_access ? "✓ Enabled" : "✗ Disabled"}
            </p>
          </div>

          <div className="mt-[2vh]">
            <h5 style={{ fontWeight: "bold" }} className="text-sm">
              Allowed Tokens
            </h5>
            <p className="text-sm text-gray-600 mt-[0.5vh]">
              {currData.allowed_requests}
            </p>
          </div>

          {currData.segregation_model_access && (
            <div className="mt-[2vh]">
              <h5 style={{ fontWeight: "bold" }} className="text-sm">
                Uploaded CXRs
              </h5>
              <p className="text-sm text-gray-600 mt-[0.5vh]">
                {currData.chest_xray_count ?? 0}
              </p>
            </div>
          )}

          <div className="mt-[2vh]">
            <h5 style={{ fontWeight: "bold" }} className="text-sm">
              Activation Date
            </h5>
            <p className="text-sm text-gray-600 mt-[0.5vh]">
              {currData.activation_date
                ? new Date(currData.activation_date).toLocaleString()
                : "Not set"}
            </p>
          </div>

          <div className="mt-[2vh]">
            <h5 style={{ fontWeight: "bold" }} className="text-sm">
              Expiry Date
            </h5>
            <p className="text-sm text-gray-600 mt-[0.5vh]">
              {currData.expiry_date
                ? new Date(currData.expiry_date).toLocaleString()
                : "Not set"}
            </p>
          </div>

          <div className="mt-[2vh]">
            <h2 style={{ fontWeight: "bold" }} className="text-lg">
              Enabled Models
            </h2>
            <Separator className="mt-[1vh]" />
            <div className="mt-[1.5vh]">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "0.5vh",
                }}
              >
                {[
                  { label: "Abnormal", value: currData.abnormal_model_access },
                  { label: "TB", value: currData.tb_model_access },
                  {
                    label: "Atelectasis",
                    value: currData.atelectasis_model_access,
                  },
                  { label: "Edema", value: currData.edema_model_access },
                  {
                    label: "Cardiomegaly",
                    value: currData.cardiomegaly_model_access,
                  },
                  {
                    label: "Consolidation",
                    value: currData.consolidation_model_access,
                  },
                  {
                    label: "Rheumatic",
                    value: currData.rheumatic_model_access,
                  },
                  {
                    label: "Pneumothorax",
                    value: currData.pneumothorax_model_access,
                  },
                  { label: "Effusion", value: currData.effusion_model_access },
                  {
                    label: "Segregation",
                    value: currData.segregation_model_access,
                  },
                ].map((model) => (
                  <div key={model.label} className="text-xs">
                    <span
                      className={
                        model.value
                          ? "text-green-600 font-semibold"
                          : "text-gray-400"
                      }
                    >
                      {model.value ? "✓" : "○"} {model.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Alert
        open={updateAlert}
        setOpen={setUpdateAlert}
        title="Update API Configuration"
        description="Are you sure you want to save these changes to the API configuration?"
        action={updateApi}
      />
    </div>
  );
}
